<template>
  <div class="sample-documents-section">
    <div class="section-header">
      <div class="header-icon">
        <i class="fas fa-file-signature"></i>
      </div>
      <div class="header-content">
        <h3>Sample Documents</h3>
        <p class="section-description">
          Upload sample documents to help users understand what compliant documents should look like. 
          These samples will be provided to users when requesting document uploads.
        </p>
      </div>
    </div>

    <div v-if="error" class="error-message">
      {{ error }}
    </div>

    <!-- Upload Section -->
    <div class="upload-section">
      <h4>Upload New Sample Document</h4>
      <div class="upload-form">
        <div class="form-row">
          <div class="form-group">
            <label for="sample-type">Document Type</label>
            <select 
              id="sample-type" 
              v-model="uploadForm.documentType" 
              class="form-control"
              :disabled="uploading"
            >
              <option value="">Select document type...</option>
              <option value="COI">Certificate of Insurance (COI)</option>
              <option value="CONTRACT">Contract</option>
              <option value="POLICY">Insurance Policy</option>
              <option value="ENDORSEMENT">Policy Endorsement</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          
          <div class="form-group">
            <label for="sample-description">Description</label>
            <input 
              id="sample-description"
              type="text" 
              v-model="uploadForm.description" 
              class="form-control"
              placeholder="e.g., 'Good example of compliant COI with all required coverages'"
              :disabled="uploading"
            />
          </div>
        </div>

        <div class="file-upload-area" 
             :class="{ 'dragging': isDragging, 'disabled': uploading }"
             @drop="handleDrop"
             @dragover.prevent
             @dragenter.prevent="isDragging = true"
             @dragleave.prevent="isDragging = false">
          <input 
            type="file" 
            ref="fileInput" 
            @change="handleFileSelect"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            style="display: none"
            :disabled="uploading"
          />
          
          <div class="upload-content">
            <i class="fas fa-cloud-upload-alt"></i>
            <p v-if="uploadForm.selectedFile">
              <strong>{{ uploadForm.selectedFile.name }}</strong>
            </p>
            <p v-else>
              Drag and drop a file here, or 
              <button @click="$refs.fileInput.click()" type="button" class="link-button" :disabled="uploading">
                browse files
              </button>
            </p>
            <small>Supported formats: PDF, DOC, DOCX, PNG, JPG</small>
          </div>
        </div>

        <div class="upload-actions">
          <button 
            @click="uploadSampleDocument" 
            class="btn btn-primary"
            :disabled="!canUpload || uploading"
          >
            <i v-if="uploading" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-upload"></i>
            {{ uploading ? 'Uploading...' : 'Upload Sample' }}
          </button>
          <button 
            @click="clearForm" 
            class="btn btn-secondary"
            :disabled="uploading"
          >
            Clear
          </button>
        </div>
      </div>
    </div>

    <!-- Sample Documents List -->
    <div class="samples-list-section">
      <div class="list-header">
        <h4>Current Sample Documents ({{ sampleDocuments.length }})</h4>
        <button @click="loadSampleDocuments" class="btn btn-outline-secondary btn-sm" :disabled="loading">
          <i class="fas fa-refresh" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>

      <div v-if="loading && sampleDocuments.length === 0" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        Loading sample documents...
      </div>

      <div v-else-if="sampleDocuments.length === 0" class="empty-state">
        <i class="fas fa-file-signature"></i>
        <h5>No Sample Documents</h5>
        <p>Upload your first sample document to help users understand what compliant documents should look like.</p>
      </div>

      <div v-else class="samples-grid">
        <div v-for="document in sampleDocuments" :key="document.id" class="sample-card">
          <div class="sample-header">
            <div class="sample-icon">
              <i :class="getDocumentIcon(document.document_type)"></i>
            </div>
            <div class="sample-info">
              <h5>{{ document.name }}</h5>
              <span class="document-type-badge" :class="document.document_type.toLowerCase()">
                {{ getDocumentTypeLabel(document.document_type) }}
              </span>
            </div>
            <div class="sample-actions">
              <button @click="downloadDocument(document)" class="btn btn-sm btn-outline-primary">
                <i class="fas fa-download"></i>
              </button>
              <button @click="deleteDocument(document)" class="btn btn-sm btn-outline-danger">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
          
          <div class="sample-description">
            <p>{{ document.description || 'No description provided' }}</p>
          </div>
          
          <div class="sample-metadata">
            <small class="upload-date">
              Uploaded {{ formatDate(document.created_at) }}
            </small>
            <small class="file-size">
              {{ formatFileSize(document.file_size) }}
            </small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'SampleDocuments',
  props: {
    projectId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      sampleDocuments: [],
      loading: false,
      uploading: false,
      error: null,
      isDragging: false,
      uploadForm: {
        documentType: '',
        description: '',
        selectedFile: null
      }
    };
  },
  computed: {
    canUpload() {
      return this.uploadForm.documentType && 
             this.uploadForm.description && 
             this.uploadForm.selectedFile;
    }
  },
  async mounted() {
    await this.loadSampleDocuments();
  },
  methods: {
    async loadSampleDocuments() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await api.get(`/projects/${this.projectId}/sample-documents/`);
        this.sampleDocuments = response.data.results || response.data || [];
      } catch (error) {
        console.error('Error loading sample documents:', error);
        this.error = 'Failed to load sample documents. Using demo data for now.';
        // Fallback demo data
        this.sampleDocuments = [
          {
            id: 1,
            name: 'Sample_COI_Complete.pdf',
            document_type: 'COI',
            description: 'Example of a compliant COI with all required coverages and proper additional insured language',
            created_at: '2024-01-15T10:30:00Z',
            file_size: 256000
          },
          {
            id: 2,
            name: 'Master_Service_Agreement_Template.pdf',
            document_type: 'CONTRACT',
            description: 'Standard contract template with typical insurance requirements',
            created_at: '2024-01-10T14:20:00Z',
            file_size: 512000
          }
        ];
      } finally {
        this.loading = false;
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadForm.selectedFile = file;
      }
    },

    handleDrop(event) {
      event.preventDefault();
      this.isDragging = false;
      
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.uploadForm.selectedFile = files[0];
      }
    },

    async uploadSampleDocument() {
      if (!this.canUpload) return;

      this.uploading = true;
      this.error = null;

      try {
        const formData = new FormData();
        formData.append('file', this.uploadForm.selectedFile);
        formData.append('name', this.uploadForm.selectedFile.name); // Add the filename as name
        formData.append('document_type', this.uploadForm.documentType);
        formData.append('description', this.uploadForm.description);
        // project is set automatically by the ViewSet from URL path

        const response = await api.post(`/projects/${this.projectId}/sample-documents/`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        // Add to local list
        this.sampleDocuments.unshift(response.data);
        this.clearForm();
        
        this.$emit('sample-uploaded', response.data);
        
        // Show success message
        if (this.$toast) {
          this.$toast.success('Sample document uploaded successfully!');
        }
        
      } catch (error) {
        console.error('Error uploading sample document:', error);
        this.error = 'Failed to upload sample document. ' + (error.response?.data?.detail || error.message || '');
      } finally {
        this.uploading = false;
      }
    },

    clearForm() {
      this.uploadForm = {
        documentType: '',
        description: '',
        selectedFile: null
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async deleteDocument(document) {
      if (!confirm(`Are you sure you want to delete "${document.name}"? This action cannot be undone.`)) {
        return;
      }

      try {
        await api.delete(`/projects/${this.projectId}/sample-documents/${document.id}/`);
        
        // Remove from local list
        this.sampleDocuments = this.sampleDocuments.filter(d => d.id !== document.id);
        
        this.$emit('sample-deleted', document);
        
        if (this.$toast) {
          this.$toast.success('Sample document deleted successfully!');
        }
      } catch (error) {
        console.error('Error deleting sample document:', error);
        this.error = 'Failed to delete sample document. ' + (error.response?.data?.detail || error.message || '');
      }
    },

    async downloadDocument(doc) {
      try {
        const response = await api.get(`/projects/${this.projectId}/sample-documents/${doc.id}/download/`, {
          responseType: 'blob',
          maxRedirects: 5, // Allow redirects to S3 URLs
        });
        
        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
      } catch (error) {
        console.error('Error downloading sample document:', error);
        this.error = 'Failed to download sample document. ' + (error.response?.data?.detail || error.message || '');
      }
    },

    getDocumentIcon(type) {
      const icons = {
        'COI': 'fas fa-shield-alt',
        'CONTRACT': 'fas fa-file-contract',
        'POLICY': 'fas fa-file-invoice',
        'ENDORSEMENT': 'fas fa-file-plus',
        'OTHER': 'fas fa-file-alt'
      };
      return icons[type] || 'fas fa-file';
    },

    getDocumentTypeLabel(type) {
      const labels = {
        'COI': 'Certificate of Insurance',
        'CONTRACT': 'Contract',
        'POLICY': 'Insurance Policy',
        'ENDORSEMENT': 'Policy Endorsement',
        'OTHER': 'Other'
      };
      return labels[type] || type;
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    formatFileSize(bytes) {
      if (!bytes) return '0 B';
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
    }
  }
};
</script>

<style scoped>
.sample-documents-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 24px;
}

.section-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.header-content h3 {
  margin: 0 0 8px 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.section-description {
  margin: 0;
  color: #6b7280;
  line-height: 1.5;
}

.error-message {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.upload-section {
  margin-bottom: 32px;
}

.upload-section h4 {
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  margin-bottom: 4px;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.form-control {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.file-upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  transition: all 0.2s ease;
  margin-bottom: 16px;
}

.file-upload-area:hover {
  border-color: #667eea;
  background-color: #f8faff;
}

.file-upload-area.dragging {
  border-color: #667eea;
  background-color: #f0f4ff;
}

.file-upload-area.disabled {
  opacity: 0.6;
  pointer-events: none;
}

.upload-content i {
  font-size: 2rem;
  color: #9ca3af;
  margin-bottom: 12px;
}

.upload-content p {
  margin: 0 0 8px 0;
  color: #374151;
}

.upload-content small {
  color: #6b7280;
}

.link-button {
  background: none;
  border: none;
  color: #667eea;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
}

.link-button:hover {
  color: #4f46e5;
}

.upload-actions {
  display: flex;
  gap: 12px;
}

.btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  text-decoration: none;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background-color: #667eea;
  color: white;
  border-color: #667eea;
}

.btn-primary:hover:not(:disabled) {
  background-color: #5a67d8;
  border-color: #5a67d8;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e5e7eb;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.list-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 0.875rem;
}

.btn-outline-secondary {
  background-color: transparent;
  color: #6b7280;
  border-color: #d1d5db;
}

.btn-outline-secondary:hover:not(:disabled) {
  background-color: #f9fafb;
  color: #374151;
}

.loading-state, .empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #6b7280;
}

.loading-state i, .empty-state i {
  font-size: 2.5rem;
  margin-bottom: 12px;
  color: #d1d5db;
}

.empty-state h5 {
  margin: 0 0 8px 0;
  color: #374151;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

.samples-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 16px;
}

.sample-card {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background-color: #fafafa;
  transition: box-shadow 0.2s ease;
}

.sample-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.sample-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.sample-icon {
  width: 40px;
  height: 40px;
  background-color: #667eea;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.sample-info {
  flex-grow: 1;
}

.sample-info h5 {
  margin: 0 0 4px 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: #111827;
}

.document-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
}

.document-type-badge.coi {
  background-color: #dbeafe;
  color: #1e40af;
}

.document-type-badge.contract {
  background-color: #d1fae5;
  color: #065f46;
}

.document-type-badge.policy {
  background-color: #fef3c7;
  color: #92400e;
}

.document-type-badge.endorsement {
  background-color: #ede9fe;
  color: #5b21b6;
}

.document-type-badge.other {
  background-color: #f3f4f6;
  color: #374151;
}

.sample-actions {
  display: flex;
  gap: 4px;
}

.btn-outline-primary {
  background-color: transparent;
  color: #667eea;
  border-color: #667eea;
}

.btn-outline-primary:hover:not(:disabled) {
  background-color: #667eea;
  color: white;
}

.btn-outline-danger {
  background-color: transparent;
  color: #dc2626;
  border-color: #dc2626;
}

.btn-outline-danger:hover:not(:disabled) {
  background-color: #dc2626;
  color: white;
}

.sample-description {
  margin-bottom: 8px;
}

.sample-description p {
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.4;
}

.sample-metadata {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.sample-metadata small {
  color: #9ca3af;
  font-size: 0.75rem;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .samples-grid {
    grid-template-columns: 1fr;
  }
  
  .upload-actions {
    flex-direction: column;
  }
  
  .list-header {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
}
</style>